import React, { memo, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { useReduxSelector } from "@hooks/useReduxSelector";
import { SvgSortBy } from "@routes/IconeSvg";
import {
  disciplinaryCategoryActions,
  disciplinaryEnterpriseActions,
  disciplinaryGradationActions
} from "@store/actions";
import { setEnterpriseDisciplinary } from "@store/actions/disciplinary/_disciplinaryAction";
import moment from "moment-timezone";

import DotLoader from "@components/tailwindComponents/DotLoader";
import EmptyData from "@components/tailwindComponents/EmptyData";

import { getInitPeriodFilterMonthe } from "@helpers/_functions";

import DisciplinaryItemTable from "./DisciplinaryItemTable";
import Filter from "./disciplinaryFilter";
import { SORT_FUNCTION } from "./utils";

const initFilter = {
  period: getInitPeriodFilterMonthe(),
  employees: [],
  category: [],
  gradation: []
};

const SuivisDisciplinary = () => {
  const { t: translator, i18n } = useTranslation("disciplinary.disciplinary");
  const [filtersLists, setFiltersLists] = useState(initFilter);
  const [selectedSortType, setSelectedSortType] = useState("period_desc");
  const reduxDispatch = useDispatch();

  useEffect(() => {
    reduxDispatch(disciplinaryEnterpriseActions.loadData);
    reduxDispatch(disciplinaryCategoryActions.loadData);
    reduxDispatch(disciplinaryGradationActions.loadData);
  }, []);

  const disciplinaryList = useReduxSelector(
    ({ disciplinary }) => disciplinary.disciplinary.list
  );

  const isQueryProcessing = useReduxSelector(
    ({ disciplinary }) => disciplinary.disciplinary.isQueryProcessing
  );

  const getDataFilter = async (filters, dateChanged) => {
    const { period = getInitPeriodFilterMonthe() } = filters;
    reduxDispatch(setEnterpriseDisciplinary({ isQueryProcessing: true }));
    if (dateChanged) {
      await reduxDispatch({
        type: "disciplinary/SET_DISCIPLINARY",
        payload: { period }
      });
      await reduxDispatch(disciplinaryEnterpriseActions.loadData);
    }
    setFiltersLists(filters);
  };

  const disciplinaryFiltered = useMemo(() => {
    if (!disciplinaryList) {
      return [];
    }

    const filteredList = disciplinaryList.filter((disciplinary) => {
      const startDate = moment(disciplinary.startDate).format("YYYY-MM-DD");
      const endDate = moment(disciplinary.endDate).format("YYYY-MM-DD");
      const startDateFilter = moment(filtersLists.period.startDate).format(
        "YYYY-MM-DD"
      );
      const endDateFilter = moment(filtersLists.period.endDate).format(
        "YYYY-MM-DD"
      );

      if (
        !(
          (startDate > startDateFilter && startDate < endDateFilter) ||
          (endDate > startDateFilter && endDate < endDateFilter) ||
          startDate === startDateFilter ||
          startDate === endDateFilter ||
          endDate === endDateFilter ||
          endDate === startDateFilter
        )
      ) {
        return false;
      }

      if (
        filtersLists.category.length &&
        !filtersLists.category.some(
          (_category) => _category._id === disciplinary.category?._id
        )
      ) {
        return false;
      }

      if (
        filtersLists.gradation.length &&
        !filtersLists.gradation.some(
          (_gradation) => _gradation._id === disciplinary.gradation?._id
        )
      ) {
        return false;
      }

      if (
        filtersLists.employees.length &&
        !filtersLists.employees.some(
          (employee) => employee.id === disciplinary.employer._id
        )
      ) {
        return false;
      }

      return true;
    });

    return filteredList;
  }, [disciplinaryList, filtersLists]);

  useEffect(() => {
    reduxDispatch(setEnterpriseDisciplinary({ isQueryProcessing: false }));
  }, [reduxDispatch, disciplinaryFiltered]);

  const sortedDisciplinaryList = useMemo(
    () => [...disciplinaryFiltered].sort(SORT_FUNCTION[selectedSortType]),
    [selectedSortType, disciplinaryFiltered]
  );

  return (
    <div className=" arh-rounded arh-bg-white arh-p-6">
      <Filter
        t={translator}
        setDataFilter={getDataFilter}
      />

      <div className="arh-h-full arh-max-h-screen arh-w-full arh-overflow-auto">
        <table className="arh-mt-7 arh-h-auto arh-w-full arh-table-auto arh-items-center arh-font-poppins">
          <thead className="arh-sticky -arh-top-[1px] arh-z-[2]">
            <tr className="arh arh-border arh-border-x-0 arh-border-b arh-border-t-0 arh-border-solid arh-border-greyBorderInput arh-px-5 arh-font-poppins arh-text-xs arh-font-semibold arh-text-darkColor">
              <th className="arh-bg-greyColor arh-w-[15%] arh-px-5 arh-py-3 arh-text-xs">
                {translator("Employé")}
              </th>
              <th className="arh-bg-greyColor arh-w-[15%] arh-px-5 arh-py-3 arh-text-xs">
                {translator("Titre de l'événement")}
              </th>
              <th className="arh-bg-greyColor arh-w-[10%] arh-px-5 arh-text-xs">
                {translator("Événement")}
              </th>
              <th className="arh-bg-greyColor arh-w-[10%] arh-px-5 arh-text-xs">
                {translator("Catégorie")}
              </th>
              <th className="arh-bg-greyColor arh-w-[10%] arh-px-5 arh-text-xs">
                {translator("Gradation")}
              </th>
              <th className="arh-bg-greyColor arh-w-[15%] arh-px-5 arh-text-xs">
                <div className="arh-flex arh-items-center">
                  {translator("Date/Période")}
                  <span
                    className="arh-ml-2 arh-cursor-pointer"
                    onClick={() => {
                      if (selectedSortType === "period_asc") {
                        setSelectedSortType("period_desc");
                      } else {
                        setSelectedSortType("period_asc");
                      }
                    }}
                  >
                    {SvgSortBy()}
                  </span>
                </div>
              </th>
              <th className="arh-bg-greyColor arh-w-[7%] arh-px-5 arh-text-xs">
                {translator("Note interne")}
              </th>
              <th className="arh-bg-greyColor arh-w-[14%] arh-px-5 arh-text-xs">
                {translator("Fichier")}
              </th>
              <th className="arh-bg-greyColor arh-w-[9%] arh-px-5 arh-text-xs">
                {translator("Alerte")}
              </th>
            </tr>
          </thead>

          <tbody>
            {/* Container table */}
            {isQueryProcessing ? (
              <tr>
                <td colSpan={13}>
                  <div className="arh-h-[40dvh]">
                    <DotLoader size="small" />
                  </div>
                </td>
              </tr>
            ) : sortedDisciplinaryList?.length > 0 ? (
              sortedDisciplinaryList?.map((disciplinary) => (
                <React.Fragment key={disciplinary?._id}>
                  <DisciplinaryItemTable
                    translator={translator}
                    i18n={i18n}
                    disciplinary={disciplinary}
                  />
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan={13}>
                  <EmptyData>
                    {translator("Aucun suivi disciplinaire à afficher")}
                  </EmptyData>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default memo(SuivisDisciplinary);
